import { createLdFlagServiceConfig } from '@app/app-initializer';
import { EbbAuthService } from '@app/browser-extension/services/ebb-auth.service';
import { ExtensionLocalizationService } from '@app/browser-extension/services/extension-localization.service';
import { findOrgInfo } from '@degreed/lxp/org/utils';
import {
  BrandingDataForOrg,
  BrandingService,
  ConfigurationService,
  LDFlagsService,
  WebEnvironmentService,
} from '@degreed/lxp/services';
import { AuthService, AuthUser } from '@degreed/lxp/services/authorization';
import { TranslateService } from '@ngx-translate/core';
import { from, lastValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export function initExtensionAppState(
  enviroService: WebEnvironmentService,
  authService: AuthService,
  ebbAuthService: EbbAuthService,
  translate: TranslateService,
  configService: ConfigurationService,
  localizationService: ExtensionLocalizationService,
  launchDarklyClientService: LDFlagsService,
  brandingService: BrandingService
) {
  return () => {
    // Initialize web environment then dependent auth service sequentially
    return lastValueFrom(
      enviroService.initialize().pipe(
        switchMap(() => configService.init()),
        switchMap(() => ebbAuthService.authCheck()),
        switchMap(() =>
          initTranslations(authService.authUser, translate, localizationService)
        ),
        switchMap(() =>
          launchDarklyClientService.initialize(
            createLdFlagServiceConfig(authService, enviroService)
          )
        ),
        switchMap(() => {
          const authUser = authService.authUser;
          const orgId = authUser?.defaultOrgId;
          const orgInfo = authUser ? findOrgInfo(authUser, orgId) : null;

          // Build data for legacy branding fallback
          const brandingDataForOrg: BrandingDataForOrg | undefined = orgInfo
            ? {
                orgId: orgInfo.organizationId,
                orgName: orgInfo.name,
                orgImage: orgInfo.image,
                endorsedImage: orgInfo.endorsedImage,
                orgBranding: orgInfo.organizationBranding,
              }
            : undefined;

          return from(
            brandingService.loadBranding(
              brandingDataForOrg,
              true // reset
            )
          );
        })
      )
    );
  };
}

/**
 * Initializes the translation service which handles the bulk of our translations
 * but does not cover the initialization of the app locale which will affect
 * certain settings like dates using pipe formatters.
 *
 * Localization is set in app-browser-extension-bootstrap.module.ts
 *
 * @param translate - translation service to initialize with locales
 * @param localizationService - Only used for the constants
 */
function initTranslations(
  authUser: AuthUser,
  translate: TranslateService,
  localizationService: ExtensionLocalizationService
) {
  localizationService.userLocale = authUser?.viewerProfile?.localeId;
  translate.setDefaultLang(localizationService.defaultLocale);
  return translate.use(localizationService.userExtensionLocale);
}
